import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions, 
  Typography, 
  Button, 
  Box, 
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

import EditGalleryModal from './EditGalleryModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { getTransformedImageUrl } from '../../../utils/filestack';

const GalleryCard = ({ gallery, onView, isAdmin, onRefresh }) => {
  const { t } = useTranslation('gallery');
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleOpenEditModal = () => {
    handleMenuClose();
    setOpenEditModal(true);
  };
  
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
  };
  
  const handleOpenDeleteModal = () => {
    handleMenuClose();
    setOpenDeleteModal(true);
  };
  
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };
  
  const handleGalleryUpdated = () => {
    onRefresh();
    handleCloseEditModal();
    toast.success(t('messages.updateSuccess'));
  };
  
  const handleGalleryDeleted = () => {
    onRefresh();
    handleCloseDeleteModal();
    toast.success(t('messages.deleteSuccess'));
  };
  
  // Get the first image as the cover or use a placeholder
  const coverImage = gallery.images && gallery.images.length > 0
    ? (gallery.images[0].thumbnailUrl || getTransformedImageUrl(gallery.images[0].handle || gallery.images[0].url, 'thumbnail'))
    : 'https://via.placeholder.com/400x300?text=No+Images';
  
  return (
    <>
      <Card 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          transition: 'transform 0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          }
        }}
      >
        <CardMedia
          component="img"
          height="200"
          image={coverImage}
          alt={gallery.title}
          sx={{ objectFit: 'cover', cursor: 'pointer' }}
          onClick={onView}
        />
        
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="h6" component="h2" gutterBottom noWrap>
              {gallery.title}
            </Typography>
            
            {isAdmin && (
              <IconButton 
                size="small" 
                onClick={handleMenuOpen}
                sx={{ ml: 1, mt: -1 }}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              height: '40px'
            }}
          >
            {gallery.description || t('gallery.noDescription')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={format(new Date(gallery.date), 'PP')} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
            {gallery.event && (
              <Chip 
                icon={<EventIcon fontSize="small" />}
                label={gallery.event.title} 
                size="small" 
                color="secondary" 
              />
            )}
          </Box>
        </CardContent>
        
        <CardActions>
          <Button 
            startIcon={<VisibilityIcon />} 
            onClick={onView} 
            fullWidth
          >
            {t('actions.view')}
          </Button>
        </CardActions>
      </Card>
      
      {/* Menu for admin actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleOpenEditModal}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('actions.edit')} />
        </MenuItem>
        <MenuItem onClick={handleOpenDeleteModal}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('actions.delete')} sx={{ color: 'error.main' }} />
        </MenuItem>
      </Menu>
      
      {/* Edit Gallery Modal */}
      {openEditModal && (
        <EditGalleryModal
          open={openEditModal}
          onClose={handleCloseEditModal}
          onSuccess={handleGalleryUpdated}
          gallery={gallery}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {openDeleteModal && (
        <DeleteConfirmationModal
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={handleGalleryDeleted}
          galleryId={gallery._id}
        />
      )}
    </>
  );
};

export default GalleryCard; 